import { useEffect, useRef, useLayoutEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { fetchChatList, subscribeToChatList } from "./ChatThunk";
import * as chatV2 from "../../../modules/chat/redux/ChatSlice.v2";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { RootState } from "src/setup/redux/store";

const chatTypes: ("allChat" | "myChat")[] = ["allChat", "myChat"];

export const useChatListSubscriber = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const audioRef = useRef<HTMLAudioElement>();
  const lastToastMapRef = useRef<Record<string, number>>({});
  const lastToastTimeRef = useRef<Record<string, number>>({});
  const TOAST_THROTTLE_MS = 3000;
  const lastFetchedAtRef = useRef<Record<string, number>>({});
  const EXPIRY_MS = 60 * 1000 * 3; // 3 minute

  // Get both chat type states directly
  const allChatOrder = useSelector(
    (state: RootState) => state.ChatV2.allChat?.collaborationOrder ?? [],
    shallowEqual
  );
  const myChatOrder = useSelector(
    (state: RootState) => state.ChatV2.myChat?.collaborationOrder ?? [],
    shallowEqual
  );

  // Use refs to access latest state inside effects without adding to deps
  const allChatOrderRef = useRef(allChatOrder);
  const myChatOrderRef = useRef(myChatOrder);

  useLayoutEffect(() => {
    allChatOrderRef.current = allChatOrder;
    myChatOrderRef.current = myChatOrder;
  });

  useEffect(() => {
    if (typeof Audio !== "undefined") {
      audioRef.current = new Audio("/media/misc/audio/notification.mp3");
    }
  }, []);

  useEffect(() => {
    const unsubscribes: (() => void)[] = [];

    chatTypes.forEach((type) => {
      const now = Date.now();
      const lastFetchedAt = lastFetchedAtRef.current[type] ?? 0;
      const isExpired = now - lastFetchedAt > EXPIRY_MS;

      // Check collaborationOrder for the specific type using refs
      const collaborationOrderForType =
        type === "allChat" ? allChatOrderRef.current : myChatOrderRef.current;
      const alreadyFetched =
        Array.isArray(collaborationOrderForType) && collaborationOrderForType.length > 0;

      if (isExpired || !alreadyFetched) {
        dispatch(fetchChatList({ chatType: type, lastInteractionAt: null }));
        lastFetchedAtRef.current[type] = now;
      }

      const unsubscribe = subscribeToChatList(type, (collab, changeType) => {
        if (changeType !== "removed") {
          dispatch(
            chatV2.updateCollaboration({ chatType: type, data: collab })
          );
        } else {
          dispatch(
            chatV2.removeCollaboration({
              chatType: type,
              id: collab?.id,
            })
          );
        }

        const collabId = collab.id;
        const lastInteractionAt = collab.lastInteractionAt?.seconds ?? 0;

        const now = Date.now();
        const lastShown = lastToastMapRef.current[collabId];
        const lastToastTime = lastToastTimeRef.current[collabId] ?? 0;

        // Skip if we already showed this interaction recently
        if (
          lastShown === lastInteractionAt &&
          now - lastToastTime < TOAST_THROTTLE_MS
        )
          return;

        // Only show toast for messages less than 30 seconds old
        const MESSAGE_FRESHNESS_MS = 30 * 1000;
        const messageAge = now - (lastInteractionAt * 1000);
        if (messageAge > MESSAGE_FRESHNESS_MS) {
          return;
        }

        lastToastMapRef.current[collabId] = lastInteractionAt;
        lastToastTimeRef.current[collabId] = now;

        const profile =
          collab?.profileName ??
          collab?.userSearchKeys?.[collab?.lastInteractionChannel] ??
          "";
        const msg = collab?.lastInteractionMessage ?? "";

        if (collab?.lastInteractionDestination === "inbound") {
          toast.info(`${t("Notif.Info.Message")} ${profile} : ${msg}`);
          audioRef.current?.play().catch(() => {});
        }
      });

      unsubscribes.push(unsubscribe);
    });

    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, t]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const now = Date.now();

        chatTypes.forEach((type) => {
          const lastFetchedAt = lastFetchedAtRef.current[type] ?? 0;
          const isExpired = now - lastFetchedAt > EXPIRY_MS;

          if (isExpired) {
            dispatch(
              fetchChatList({ chatType: type, lastInteractionAt: null })
            );
            lastFetchedAtRef.current[type] = now;
          }
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dispatch]);
};
